import React, { useState, useEffect } from "react";
import BitrixAPI from "../api/b24";
import { FilesContainer } from "../components/FileViewer";
import LoadingButton from "../components/LoadingButton";
import { useHeader } from "../contexts/HeaderContext";

const ArgonOrdersPageB24 = () => {
  const [argonOrders, setArgonOrders] = useState([]);
  const [inWorkOrders, setInWorkOrders] = useState([]);
  const [doneOrders, setDoneOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("argonOrder");
  const [error, setError] = useState(null);
  const [processingOrders, setProcessingOrders] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const { setHeaderExtras } = useHeader();

  const currentOrders =
    activeTab === "argonOrder"
      ? argonOrders
      : activeTab === "inWork"
      ? inWorkOrders
      : doneOrders;

  useEffect(() => {
    setHeaderExtras(
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <input
          type="text"
          placeholder="Поиск по номеру, названию..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
          style={{ width: "220px" }}
        />
        <span className="text-sm text-gray-600">
          Заказов: <strong>{currentOrders.length}</strong>
        </span>
      </div>
    );
    return () => setHeaderExtras(null);
  }, [searchTerm, currentOrders.length]);

  const fetchData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) setLoading(true);
      setError(null);

      if (activeTab === "argonOrder") {
        const data = await BitrixAPI.getArgonOrders();
        setArgonOrders(data);
      } else if (activeTab === "inWork") {
        const data = await BitrixAPI.getArgonInWorkOrders();
        setInWorkOrders(data);
      } else if (activeTab === "done") {
        const data = await BitrixAPI.getArgonDoneOrders();
        setDoneOrders(data);
      }
    } catch (err) {
      console.error("Ошибка загрузки заказов аргон:", err);
      setError(`Ошибка загрузки: ${err.message}`);
    } finally {
      if (isInitialLoad) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(true);
  }, [activeTab]);

  const handleMoveToInWork = async (orderId) => {
    try {
      setProcessingOrders((prev) => new Set([...prev, orderId]));
      const result = await BitrixAPI.updateStageArgonInWork(orderId);
      if (result.success) {
        setArgonOrders((prev) => prev.filter((o) => o.id !== orderId));
      } else {
        throw new Error("Не удалось обновить стадию");
      }
    } catch (err) {
      setError(`Ошибка: ${err.message}`);
    } finally {
      setProcessingOrders((prev) => {
        const s = new Set(prev);
        s.delete(orderId);
        return s;
      });
    }
  };

  const handleMoveToDone = async (orderId) => {
    try {
      setProcessingOrders((prev) => new Set([...prev, orderId]));
      const result = await BitrixAPI.updateStageArgonDone(orderId);
      if (result.success) {
        setInWorkOrders((prev) => prev.filter((o) => o.id !== orderId));
      } else {
        throw new Error("Не удалось обновить стадию");
      }
    } catch (err) {
      setError(`Ошибка: ${err.message}`);
    } finally {
      setProcessingOrders((prev) => {
        const s = new Set(prev);
        s.delete(orderId);
        return s;
      });
    }
  };

  const filteredOrders = currentOrders.filter((order) => {
    const q = searchTerm.toLowerCase();
    return (
      (order.orderNumber || "").toLowerCase().includes(q) ||
      (order.title || "").toLowerCase().includes(q) ||
      String(order.id).includes(q)
    );
  });

  const renderOrdersList = () => {
    if (filteredOrders.length === 0) {
      return (
        <div className="bg-white p-4 shadow rounded-lg">
          <p className="text-gray-600">Нет заказов для отображения</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {filteredOrders.map((order) => (
          <div
            key={order.id}
            className="bg-white p-4 shadow rounded-lg hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-lg font-medium">Заказ №{order.orderNumber}</h2>
                <p className="text-gray-600">{order.title}</p>
                <p className="text-xs text-gray-500">Deal ID: {order.id}</p>
              </div>
              <div className="flex space-x-2">
                {activeTab === "argonOrder" && (
                  <LoadingButton
                    isLoading={processingOrders.has(order.id)}
                    onClick={() => handleMoveToInWork(order.id)}
                    label="Принять в работу"
                    color="purple"
                  />
                )}
                {activeTab === "inWork" && (
                  <LoadingButton
                    isLoading={processingOrders.has(order.id)}
                    onClick={() => handleMoveToDone(order.id)}
                    label="Отправить / Готово"
                    color="green"
                  />
                )}
              </div>
            </div>

            {order.productionFiles && order.productionFiles.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Чертежи для производства:</h3>
                <FilesContainer files={order.productionFiles} dealId={order.id} />
              </div>
            )}

            {order.files && order.files.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm text-gray-800 mb-2">Деталировка к чертежу:</h3>
                <FilesContainer files={order.files} dealId={order.id} />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="container mx-auto p-4">
      {/* Вкладки */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "argonOrder"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("argonOrder")}
            >
              Заказ Аргон
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "inWork"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("inWork")}
            >
              Принято в работу
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 text-sm font-medium ${
                activeTab === "done"
                  ? "text-purple-600 border-b-2 border-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("done")}
            >
              Готово и отправлено
            </button>
          </li>
        </ul>
      </div>

      {error && (
        <div className="bg-red-50 p-4 rounded-lg mb-4 text-red-700 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="p-4 bg-white shadow rounded-lg">
          <p className="text-gray-600">Загрузка данных...</p>
        </div>
      ) : (
        renderOrdersList()
      )}
    </div>
  );
};

export default ArgonOrdersPageB24;
